import { Injectable } from '@angular/core';
import { Action, createSelector, State, StateContext } from '@ngxs/store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { bytesAs } from '@libs/bytes-as';
import { ClosCloudSpaceStats } from './types';
import { FetchCloudSpace } from './cloud.actions';
import { CloudApi } from './cloud.api';
import { SignOut } from '@apps/clos-sessions/state/auth';

export interface CloudStateModel {
  space: ClosCloudSpaceStats;
}

const defaults = {
  space: {
    available: 0,
    total: 0,
    used: 0,
  },
};

/** in MB */
const LIMIT_WARNING = 200;

@State<CloudStateModel>({
  name: 'cloud',
  defaults,
})
@Injectable()
export class CloudState {
  static space = createSelector([CloudState], ({ space }: CloudStateModel) => space);

  static isInitialized = createSelector(
    [CloudState],
    ({ space }: CloudStateModel) => space.total !== 0,
  );

  static availableSpace = (mapperFn?: (availableInBytes: number) => number) => {
    return createSelector([CloudState.space], (space: ClosCloudSpaceStats) => {
      if (typeof mapperFn === 'function') {
        return mapperFn(space.available);
      }

      return space.available;
    });
  };

  // eslint-disable-next-line @typescript-eslint/member-ordering
  static hasEnoughSpaceToUseCloud = createSelector(
    [CloudState.availableSpace(bytesAs)],
    (availableSpace: number) => availableSpace >= LIMIT_WARNING,
  );

  // eslint-disable-next-line @typescript-eslint/member-ordering
  static isUsingCloudForbidden = createSelector(
    [CloudState.hasEnoughSpaceToUseCloud],
    (hasEnoughSpaceToUseCloud: boolean) => !hasEnoughSpaceToUseCloud,
  );

  constructor(protected api: CloudApi) {}

  @Action(FetchCloudSpace)
  onFetchCloudSpace({ setState }: StateContext<CloudStateModel>): Observable<ClosCloudSpaceStats> {
    return this.api.getAvailableSpace().pipe(tap((space) => setState({ space })));
  }

  /** Account-scoped data must not survive a logout (the next account can sign in without a reload). */
  @Action(SignOut)
  protected onSignOut({ setState }: StateContext<CloudStateModel>): void {
    setState(defaults);
  }
}
